import React, { useEffect, useMemo, useState } from 'react';
import { pickBy, startCase, head, capitalize, get } from 'lodash';

import Pagination from 'partial/components/Pagination';
import SearchBar from 'partial/components/input/SearchBar';
import ThumbIcon from 'partial/components/ThumbIcon';
import { BsCircleFill } from 'react-icons/bs';
import classNames from 'classnames';
import EmptyPage from 'partial/components/EmptyPage';

import List from 'partial/components/List';
import { usePersistState } from 'helper';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import VerificationDetails from './components/VerificationDetails';
import { useSubscribersList } from './hooks';

const INITIAL_STATE = {
  keyword: '',
};

export function ForVerification() {
  const { setSubLabel } = useModuleContext();
  const params = useParams();
  const id = get(params, '*');

  const [filter, setFilter] = useState(INITIAL_STATE);
  const [deleted, setDeleted] = useState(false);

  const [page, setPage] = useState({ page: '' });

  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
        status: 'PENDING',
        per_page: 15,
      }),
    [filter, page],
  );
  const [isLoading, list, meta] = useSubscribersList(payload);

  const [selected, setSelected] = usePersistState(
    'verification-request-id',
    '',
    {
      keepOnUnmount: false,
    },
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  const onSubSelect = (item) => {
    setDeleted(false);
    setSelected(item?.uuid);
    navigate(`${item?.uuid}`);
  };

  useEffect(() => {
    if ((selected && !isLoading) || deleted || id) return;
    setSelected(head(list)?.uuid);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoading, selected, setSelected]);

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} verification request${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const callBack = () => {
    setSelected('');
    setDeleted(true);
    navigate('', { replace: true });
  };

  return (
    <section className="min-h-[calc(100vh_-_5rem)] flex p-6 bg-white drop-shadow rounded-lg flex-grow static">
      <div className="pr-4 border-r border-gray-50 min-w-[300px] flex flex-col">
        <SearchBar
          name="q"
          value={filter.q}
          onChange={setFilter}
          inputClassName="py-[1.05rem] !w-full"
          containerClassName="mb-4"
        />
        <List
          data={list}
          isLoading={isLoading}
          selectedId={selected}
          onSelectRow={onSubSelect}
          containerClassName="!w-full"
          render={(item) => {
            const name = startCase(
              capitalize(
                `${item?.profile?.first_name} ${item?.profile?.last_name}`,
              ),
            );
            return (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2 pl-2 py-1 w-full">
                  <ThumbIcon
                    className="h-8 w-8 rounded-full"
                    alt={name}
                    url={item?.profile?.avatar || ''}
                  />
                  <div>
                    <div className="text-sm">{name}</div>
                    <div
                      className={classNames(
                        'text-xs font-semibold',
                        item?.status === 'Approved'
                          ? 'text-success-400'
                          : item?.status === 'Rejected'
                          ? 'text-danger-400'
                          : 'text-inherit',
                      )}
                    >
                      {item?.status}
                    </div>
                  </div>
                </div>
                <div
                  className={classNames(
                    'transition',
                    item?.status === 'Pending' && item?.uuid !== selected
                      ? 'opacity-100'
                      : 'opacity-0',
                  )}
                >
                  <BsCircleFill className="w-3 h-3 text-danger" />
                </div>
              </div>
            );
          }}
          selectedKey="uuid"
        />
        <div className="self-center mt-8">
          <Pagination value={meta} onChange={setPage} withPage={false} />
        </div>
      </div>
      <Routes>
        <Route
          path=":id/*"
          element={<VerificationDetails callBack={callBack} />}
        />
        {list?.length > 0 && !deleted && (
          <Route
            index
            element={<Navigate replace to={`${head(list)?.uuid}`} />}
          />
        )}
        <Route
          path="*"
          element={
            <div className="flex-1">
              <EmptyPage />
            </div>
          }
        />
      </Routes>
    </section>
  );
}

export default ForVerification;
